<script setup lang="ts">
import { computed, onBeforeUnmount, onMounted, ref, watch } from 'vue'
import { PAGES } from '../constants'
import ChargeIndicator from '../ChargeIndicator.vue'
import CourseCard from './Home/CourseCard.vue'
import LinkCard from './Home/LinkCard.vue'
import SmartbookLockReason from '../SmartbookLockReason.vue'
import Onboarding from './Home/Onboarding.vue'
import { useNavigation } from '../useNavigation'
import StudentPwaButton from '@/ui-kit/student/PwaButton.vue'
import type { Student2Context } from '@/types/student2'
import { useStaticAsset } from '@/composables/useStaticAsset'
import { Holiday, useHoliday, useHolidayAssets } from '~/composables/useHoliday'

const asset = useStaticAsset()

const props = defineProps<{ context: Student2Context }>()
const emit = defineEmits<{ (e: 'open-shop'): void }>()

const { navigationState, getLink: $getLink, gotoOnClick: $gotoOnClick } = useNavigation()
const holiday = useHoliday()
const holidayAssets = useHolidayAssets()
const newYearSnowImage = `url('${asset('img/holiday-nav-newyear.svg')}')`

const showMascot = ref(false)
const smartbookIconFits = ref(false)
const container = ref<HTMLElement | null>(null)
const onboarding = ref<{ $open: () => void } | null>(null)

let observer: ResizeObserver | null = null

const smartbook = computed(() => props.context.smartbook ?? {})

const mascotFilename = computed(() => {
  if (!props.context.smartbook) return null
  const type = smartbook.value.scene
  const mood = props.context.student.tails.length ? 'sad' : 'happy'
  return asset(`img/student-v2/mascot_${type}_${mood}.png`)
})

const unitLockReason = computed(() => {
  const currentUnitId = Number(new URL(navigationState.currentLocation).pathname.split('/').pop())
  const unit = props.context.course_part.units.find(e => e.id === currentUnitId)
  return unit?.lock_reason
})

const showChargeIndicator = computed(() => {
  const currentUnitId = Number(new URL(navigationState.currentLocation).pathname.split('/').pop())
  const activeUnitIndex = props.context.course_part.units.findIndex(u => u.id === currentUnitId)

  if (!props.context.smartbook) return false
  if (props.context.student.free_mode) return false
  if (activeUnitIndex === 0) return true
  return props.context.smartbook.purchased
})

const checkContainerSize = () => {
  if (!container.value) return

  const menuGridSize = 920
  const containerWidth = container.value.getBoundingClientRect().width
  const containerHeight = container.value.getBoundingClientRect().height

  const mascotSize = 160
  const mascotPadding = 24
  showMascot.value = containerWidth > menuGridSize + (mascotSize + mascotPadding) * 2

  const iconWidth = 160
  const iconHeight = 200
  const iconPaddingX = 24
  const iconMarginTop = 48
  const iconMarginBottom = 80
  smartbookIconFits.value = containerWidth > menuGridSize + (iconWidth + iconPaddingX) * 2
  smartbookIconFits.value &&= containerHeight / 2 > iconHeight + iconMarginTop + iconMarginBottom
}

const isDemo = (page: { name: string; smartbook?: boolean }) => {
  if (page.name === 'games') return props.context.student.id === 0
  return page.smartbook && (smartbook.value.demo || unitLockReason.value === 'need_login')
}

watch(
  () => navigationState.currentLocation,
  val => {
    if (val.endsWith('#onboarding')) onboarding.value?.$open()
  }
)

onMounted(() => {
  observer = new ResizeObserver(() => checkContainerSize())
  if (container.value) observer.observe(container.value)
  checkContainerSize()

  if (props.context.show_onboarding && props.context.smartbook?.unlocked) {
    onboarding.value?.$open()
  }
})

onBeforeUnmount(() => {
  observer?.disconnect()
})
</script>

<template>
  <section
    ref="container"
    class="student-cabinet__page student-main-page"
    :class="{
      'student-main-page_bottom-locked': !smartbook.unlocked && unitLockReason !== 'need_login',
      'student-main-page_newyear-bg': holiday === Holiday.NewYear
    }"
  >
    <v-modal persistent :model-value="!smartbook.unlocked && unitLockReason === 'need_login'">
      <v-unauthorized-card level="user" title="Юнит недоступен">
        В демо-кабинете ученика доступен только первый юнит. Для доступа к другим юнитам войдите на сайт МирГоворит или
        зарегистрируйтесь.

        <template #actions="{ urls }">
          <v-button :href="urls.baseLoginUrl" mode="primary filled block large" class="mt-2">
            Войти или зарегистрироваться
          </v-button>
          <v-button
            :href="$getLink({ tab: 'home', unitId: context.course_part.units[0].id })"
            mode="primary flat block large"
            class="mt-2"
            @click="$gotoOnClick"
          >
            Попробовать Unit 1
          </v-button>
        </template>
      </v-unauthorized-card>
    </v-modal>

    <div v-if="showMascot && mascotFilename" class="student-main-page__mascot">
      <img :src="mascotFilename" />

      <v-button
        v-if="context.student.tails.length"
        mode="primary flat block"
        :href="$getLink({ tab: 'tails' })"
        @click="$gotoOnClick"
      >
        {{ context.student.tails.length }}
        <img height="28" :src="asset('img/animals/animal-tail-11.png')" class="ml-1" />
      </v-button>
    </div>

    <div
      v-if="smartbookIconFits && context.smartbook"
      class="student-main-page__smartbook-icon"
      :class="{ 'student-main-page__smartbook-icon_disabled': !smartbook.unlocked }"
    >
      <img :src="asset('img/smartbook-logo.svg')" />
      <v-link
        mode="primary"
        class="text-button mt-2"
        href="https://promo.mirgovorit.ru/"
        target="_blank"
        style="pointer-events: auto"
      >
        Что такое Умный Учебник?
      </v-link>
    </div>

    <div v-if="smartbookIconFits && holidayAssets.foregroundPic" class="student-main-page__holiday-pic">
      <img :src="holidayAssets.foregroundPic" />
    </div>

    <ul class="student-main-page__cards-grid" data-nav-grid>
      <Onboarding ref="onboarding" />
      <ChargeIndicator v-if="showChargeIndicator" :context="context" class="student-main-page__charge-indicator" />

      <div v-if="!smartbook.unlocked && unitLockReason !== 'need_login'" class="student-main-page__lock">
        <SmartbookLockReason :context="context" />
      </div>

      <CourseCard data-course-card :context="context" @click:shop="emit('open-shop')" />
      <LinkCard
        v-for="page in PAGES.filter(e => !e.hideOnHomePage)"
        :key="page.name"
        :data-page-name="page.name"
        :name="page.name"
        :demo="isDemo(page)"
        :smartbook="page.smartbook"
        :dev-state="context.smartbook.development_state"
        :href="$getLink({ tab: page.name })"
        @click="$gotoOnClick"
      />
    </ul>

    <StudentPwaButton class="student-main-page__pwa-btn" />
  </section>
</template>

<style lang="scss">
@use '~/styles/pages/student2/screen' as s2;

.student-main-page {
  display: flex;
  align-items: center;
  justify-content: center;

  overflow-y: unset;

  &::before {
    content: ' ';

    background: $lightenGreen;
    width: 100%;
    height: 50%;

    position: absolute;
    left: 0;
    top: 50%;
    bottom: 0;
    z-index: -1;

    @include s2.mobile {
      top: calc(50% + 8px);
      height: calc(50% - 8px);
    }
  }

  &_bottom-locked::before {
    background: $lightenGray;
  }

  &_newyear-bg {
    background-image: v-bind(newYearSnowImage);
  }

  &__mascot {
    position: absolute;
    top: 100px;
    bottom: calc(50% + 100px);
    left: calc(((100% - 920px) / 4) - 80px);
    width: 160px;

    display: flex;
    flex-direction: column;
    gap: 24px;

    & > img {
      width: 160px;
      pointer-events: none;
    }
  }

  &__smartbook-icon {
    position: absolute;
    top: calc(50% + 48px);
    left: calc(((100% - 920px) / 4) - 80px);

    &,
    & > img {
      width: 160px;
      height: 160px;
      pointer-events: none;
    }

    &_disabled {
      opacity: 0.5;
    }
  }

  &__holiday-pic {
    position: absolute;
    pointer-events: none;
    right: -88px;
    bottom: 80px;
    width: 300px;
  }

  &__teacher-icon {
    background: $lightenBlue;
    color: $blue;
    padding: 16px;
    border: 1px solid $lightBlue;
    border-radius: 8px;

    @include s2.mobile {
      padding: 8px;
    }
  }

  &__cards-grid {
    display: grid;
    grid-template-columns: repeat(4, 1fr);
    grid-template-rows: 1fr 48px 1fr;
    gap: 32px 40px;

    max-height: calc(100% - (var(--header_height, 0) * 2) - 24px);
    max-width: calc(100% - 48px);
    margin: 16px 24px 0;

    width: 920px;
    height: 600px;

    & > *:nth-last-child(4) {
      grid-row: 3;
      grid-column: 1;
    }
    & > *:nth-last-child(3) {
      grid-row: 3;
      grid-column: 2;
    }
    & > *:nth-last-child(2) {
      grid-row: 3;
      grid-column: 3;
    }
    & > *:nth-last-child(1) {
      grid-row: 3;
      grid-column: 4;
    }

    @include s2.mobile {
      gap: 12px 16px;
      max-height: calc(100% - (var(--header_height) * 2));
      max-width: 150vmin;
      grid-template-rows: 1fr 24px 1fr;
      margin: 8px 16px 0;
    }
  }

  &__charge-indicator {
    grid-row: 2;
    grid-column: 1 / -1;
  }

  &__lock {
    grid-row: 3;
    grid-column: 1 / -1;
    z-index: 1;

    background: rgba($lightenGray, 0.7);

    display: flex;
    flex-direction: column;
    gap: 24px;
    justify-content: center;
    align-items: center;

    @include s2.mobile {
      gap: 16px;
    }
  }

  &__pwa-btn {
    position: absolute;
    bottom: 24px;
    left: 24px;

    @include s2.mobile {
      bottom: 0;
    }
  }
}
</style>
